import React, { useCallback, useMemo } from 'react';
import { gql } from '@apollo/react-hooks';
import type { ActionFn, Action, DataGridProps, SetIsLoading } from '#/common/components/DataGrid';
import useToast from '#/common/hooks/useToast';
import useSession from '#/common/hooks/useSession';
import DataGrid from '#/common/components/DataGrid';
import { getAssetList } from '#/graphql/queries';
import useAssetDelete from '../../hooks/useDeleteAssetMutation';
import columns from './columns';
import { actions } from './actions';
import { Asset } from '#/graphql/types';
import useManufacturerDataStore from '../../manufacturer/hooks/useManufacturerDataStore';
import { UserRole } from '#/common/types/userTypes';

const getAssetListQuery = gql(getAssetList);

const userSelectedActionsPerRole: Record<UserRole, string[]> = {
  [UserRole.asset_manager]: ['delete-assets'],
  [UserRole.trane_admin]: ['delete-assets'],
  [UserRole.technician]: [],
};

const gridActionsPerRole: Record<UserRole, string[]> = {
  [UserRole.asset_manager]: ['new'],
  [UserRole.trane_admin]: ['new'],
  [UserRole.technician]: [],
};

const gridOptionsPerRole: Record<UserRole, Partial<DataGridProps<Asset>>> = {
  [UserRole.asset_manager]: { selectionMode: 'multiple' },
  [UserRole.trane_admin]: { selectionMode: 'multiple' },
  [UserRole.technician]: { selectionMode: 'none' },
};

const AssetDataGrid: React.FC = () => {
  const { confirm } = useToast();
  const { user } = useSession();
  const { deleteAsset } = useAssetDelete();

  const role = user?.role as UserRole | undefined;

  const deleteItem = useCallback(
    async (ids: string[], { setIsLoading }: { setIsLoading?: SetIsLoading }) => {
      if (!ids) {
        return undefined;
      }
      const sure = await confirm('error', `Are you sure you want to delete these assets?`);
      if (!sure) {
        return;
      }
      setIsLoading && setIsLoading(true, 'Deleting data...');
      await deleteAsset(ids.join(','));
      setIsLoading && setIsLoading(false);
    },
    [deleteAsset, confirm],
  );

  const userSelectedActions: ActionFn<Asset>[] = useMemo(() => {
    const res: ActionFn<Asset>[] = [];

    if (role && userSelectedActionsPerRole[role]?.includes('delete-assets')) {
      res.push({
        key: 'delete-assets',
        icon: 'trash',
        text: 'Delete',
        onClick: async (e) => {
          await deleteItem(
            e.selectedKeys.map((i: any) => i.id || i),
            { setIsLoading: e.setIsLoading },
          );
          e.dg?.instance.clearSelection();
          e.dg?.instance.refresh();
        },
      });
    }

    return res;
  }, [role, deleteItem]);

  const gridActions = useMemo<Action<Asset>[]>(() => {
    return actions
      .filter((a) => typeof a !== 'object' || (role && gridActionsPerRole[role]?.includes(a?.key)))
      .map((a) => {
        if (typeof a === 'string') {
          return a;
        }
        const result = { ...a };
        return result;
      })
      .filter((a) => !!a);
  }, [role]);

  const manStore = useManufacturerDataStore({
    cacheEnabled: true,
    searchKey: 'name',
  });

  if (!role) return null;

  return (
    <DataGrid
      id="assets"
      pluralText="Assets"
      singularText="Asset"
      columns={columns}
      recordUrl="/assets/:id"
      actions={gridActions}
      selectedActions={userSelectedActions}
      getItemsQuery={getAssetListQuery}
      {...gridOptionsPerRole[role]}
    />
  );
};

export default React.memo(AssetDataGrid);
